import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { BulkSearchService } from '../../../services/bulk-search.service';
import { GlobalLoaderService } from '../../global-loader/global-loader.service';
import { GenericCommanService } from '../../../services/generic-common.service';
import { ProjectHistoryService } from '../../../services/project-history.service';
import { TaskProgressWebSocketService, WsProgressMessage } from '../../../services/task-progress-websocket.service';
import { merge, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { AreaChartComponent } from '../../charts/area-chart/area-chart.component';
import { CommonCircleLoaderComponent } from '../../components/common-circle-loader/common-circle-loader.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
// import { TaskStatus } from '../../models/bulk-search.models';

@Component({
  selector: 'app-generic-landing-page-audit',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    CompositeScoreWidgetComponent,
    AreaChartComponent,
    CommonCircleLoaderComponent,
    // CitationCardComponent
  ],
  templateUrl: './generic-landing-page-audit.component.html',
  styleUrl: './generic-landing-page-audit.component.scss'
})
export class GenericLandingPageAuditComponent implements OnInit, OnDestroy {
  auditResult: any = null;
  auditError: string | null = null;
  loading: boolean = false;
  private subscriptions: Subscription[] = [];

  // Properties for display (single page - backward compatibility)
  compositeScore: number = 0;
  auditAreas: { [key: string]: { current: number; potential: number } } = {};
  questionsData: Array<{
    category: string;
    question: string;
    pass: number;
    evidence: string;
  }> = [];
  
  // Chart data
  areaChartData: any[] = []; // Format: [{name: "Current/Potential", series: [{name: "Category", value: score}]}]

  // Evidence sources
  evidenceSources: any[] = [];

  // Multi-page audit properties
  baseDomain: string = '';
  discoveredPages: Array<{
    url: string;
    status: 'discovering' | 'pending' | 'auditing' | 'completed' | 'failed';
    result?: any;
    compositeScore?: number;
  }> = [];
  expandedAccordions: Set<string> = new Set();
  isDiscovering: boolean = false;
  isAuditing: boolean = false;
  discoveryProgress: {
    status: 'idle' | 'discovering' | 'discovered' | 'auditing' | 'completed';
    pagesFound: number;
  } = { status: 'idle', pagesFound: 0 };
  auditProgress: {
    total: number;
    completed: number;
    current: string;
  } = { total: 0, completed: 0, current: '' };
  isMultiPageAudit: boolean = false;

  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private bulkSearchService = inject(BulkSearchService);
  private globalLoaderService = inject(GlobalLoaderService);
  private genericCommanService = inject(GenericCommanService);
  private projectHistoryService = inject(ProjectHistoryService);
  private taskProgressWs = inject(TaskProgressWebSocketService);
  reAuditLoading = false;
  private projectId: string | null = null;
  private statusPollingInterval: any = null;
  private wsSubscription: Subscription | undefined;
  private analysisWsSub: Subscription | undefined;
  private landingAuditTerminalHandled = false;
  
  constructor(
    private genericTemplateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Get projectId from route params
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'] || null;
    });
    
    // Subscribe to Landing Page audit response from service
    const landingPageAuditSub = this.genericTemplateCommonService.landingPageAuditResponse$.subscribe((response: any) => {
      if (response) {
        this.auditResult = response;
        this.processLandingPageAuditData(response);
        this.loading = false;
        this.auditError = null;
        this.cdr.detectChanges();
      }
    });

    // Subscribe to loading state
    const loadingSub = this.genericTemplateCommonService.isLoading$.subscribe(loading => {
      this.loading = loading;
    });

    // Subscribe to error state
    const errorSub = this.genericTemplateCommonService.error$.subscribe(error => {
      if (error) {
        this.auditError = error;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(landingPageAuditSub, loadingSub, errorSub);

    // Check if we already have Landing Page audit data from history or previous analysis
    const existingData = this.genericTemplateCommonService.getCurrentLandingPageAuditResponse();
    if (existingData) {
      this.auditResult = existingData;
      this.processLandingPageAuditData(existingData);
      this.loading = false;
      this.cdr.detectChanges();
    } else {
      // If no existing data, try to load from ProjectData history API (only on page load)
      const urlParams = new URLSearchParams(window.location.search);
      const projectdataId = urlParams.get('projectdataId');
      if (projectdataId) {
        this.loadAuditDataFromHistory(projectdataId);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.wsSubscription?.unsubscribe();
    this.analysisWsSub?.unsubscribe();
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
    }
  }

  private stopStatusPolling(): void {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    this.analysisWsSub?.unsubscribe();
    this.analysisWsSub = undefined;
  }

  /**
   * Start task-based audit with status polling
   */
  private startTaskBasedAudit(request: { project_id?: string; projectdata_id?: string; filters?: any }): void {
    this.landingAuditTerminalHandled = false;
    this.reAuditLoading = true;
    this.loading = true;
    this.auditError = null;
    
    // Initialize progress
    this.globalLoaderService.initializeProgress(1);
    this.globalLoaderService.updateProgress(0, 0, [], 'Starting audit...', null, 1);
    
    // Start audit task
    this.genericCommanService.processLandingPageAudit(request).subscribe({
      next: (response: any) => {
        const taskId = response.task_id;
        if (!taskId) {
          this.auditError = 'Failed to start audit: No task ID returned';
          this.reAuditLoading = false;
          this.loading = false;
          return;
        }
        
        // Start polling status
        this.startStatusPolling(taskId);
      },
      error: (error: any) => {
        console.error('Error starting audit:', error);
        this.auditError = error.error?.detail || 'Failed to start audit';
        this.reAuditLoading = false;
        this.loading = false;
        this.globalLoaderService.resetLoader();
      }
    });
  }

  /**
   * Poll audit task status (or subscribe to WebSocket when environment.useWebSocket).
   */
  private startStatusPolling(taskId: string): void {
    const applyStatus = (status: any) => {
      const currentStep = status.current_step || 'Processing...';
      const stepWithContext = currentStep.includes('landing-page') ? currentStep : `landing-page-audit: ${currentStep}`;
      this.globalLoaderService.updateProgress(
        status.progress_percentage || 0,
        0,
        [],
        stepWithContext,
        status.estimated_time_remaining,
        1
      );
    };
    const onCompleted = (status: any) => {
      if (status.result) {
        this.auditResult = status.result;
        this.processLandingPageAuditData(status.result);
        this.loading = false;
        this.reAuditLoading = false;
        this.globalLoaderService.resetLoader();
        this.genericTemplateCommonService.updateLandingPageAuditResponse(status.result);
        this.snackBar.open('Landing Page audit completed! Results are shown below.', 'Close', { duration: 5000 });
      } else {
        this.auditError = 'Audit completed but no result available';
        this.loading = false;
        this.reAuditLoading = false;
        this.globalLoaderService.resetLoader();
      }
      this.cdr.detectChanges();
    };
    const onFailed = (status: any) => {
      this.auditError = status.error_message || 'Audit failed';
      this.loading = false;
      this.reAuditLoading = false;
      this.globalLoaderService.resetLoader();
      this.cdr.detectChanges();
    };

    if (this.taskProgressWs.useWebSocket) {
      this.stopStatusPolling();
      this.taskProgressWs.connect();
      const { projectId: pid, projectdataId: pdid } = this.genericTemplateCommonService.getCurrentProjectContext();
      const auditStreams: Observable<WsProgressMessage>[] = [this.taskProgressWs.messagesByPath('audit')];
      if (pid && pdid) {
        auditStreams.unshift(this.taskProgressWs.messagesByPath(`${pid}_${pdid}`));
      }
      this.wsSubscription = merge(...auditStreams)
        .pipe(filter((msg) => (msg.data as any)?.task_id === taskId))
        .subscribe((msg) => {
          const status = msg.data as any;
          if (!status) return;
          applyStatus(status);
          if (status.status === 'completed') {
            if (this.landingAuditTerminalHandled) return;
            this.landingAuditTerminalHandled = true;
            this.stopStatusPolling();
            onCompleted(status);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.landingAuditTerminalHandled) return;
            this.landingAuditTerminalHandled = true;
            this.stopStatusPolling();
            if (status.status === 'cancelled') {
              this.auditError = 'Audit was cancelled';
              this.loading = false;
              this.reAuditLoading = false;
              this.globalLoaderService.resetLoader();
              this.cdr.detectChanges();
            } else {
              onFailed(status);
            }
          }
          this.cdr.detectChanges();
        });

      this.analysisWsSub = this.taskProgressWs
        .messagesByPath('analysis')
        .pipe(
          filter((msg) => {
            const d = msg.data as Record<string, unknown> | undefined;
            return (
              d?.['task_id'] === taskId &&
              ['completed', 'failed', 'cancelled'].includes(String(d?.['status'] || ''))
            );
          })
        )
        .subscribe((msg) => {
          if (this.landingAuditTerminalHandled) return;
          const d = msg.data as Record<string, unknown>;
          const st = String(d['status'] || '');
          this.landingAuditTerminalHandled = true;
          this.stopStatusPolling();
          if (st === 'completed') {
            this.globalLoaderService.resetLoader();
            this.reAuditLoading = false;
            this.loading = false;
            const pd =
              pdid ||
              this.genericTemplateCommonService.getCurrentProjectContext().projectdataId ||
              new URLSearchParams(window.location.search).get('projectdataId');
            if (pd) {
              this.loadAuditDataFromHistory(pd);
              this.snackBar.open('Landing Page audit completed! Refreshing results…', 'Close', { duration: 5000 });
            } else {
              this.snackBar.open('Landing Page audit completed.', 'Close', { duration: 5000 });
            }
          } else if (st === 'cancelled') {
            this.auditError = 'Audit was cancelled';
            this.loading = false;
            this.reAuditLoading = false;
            this.globalLoaderService.resetLoader();
          } else {
            onFailed({ error_message: 'Audit failed' });
          }
          this.cdr.detectChanges();
        });
      return;
    }

    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;
    const pollStatus = () => {
      this.genericCommanService.getAuditStatus(taskId).subscribe({
        next: (status: any) => {
          pollCount++;
          applyStatus(status);
          if (status.status === 'completed') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            onCompleted(status);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            if (status.status === 'cancelled') {
              this.auditError = 'Audit was cancelled';
              this.loading = false;
              this.reAuditLoading = false;
              this.globalLoaderService.resetLoader();
            } else {
              onFailed(status);
            }
          } else {
            if (pollCount === 3 && this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = setInterval(pollStatus, regularInterval);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error polling status:', error);
          if (this.statusPollingInterval) {
            clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = null;
          }
          const errorMsg = error?.error?.detail || error?.message || 'Failed to get audit status';
          this.auditError = `Audit status check failed: ${errorMsg}. Please try re-running the audit.`;
          this.loading = false;
          this.reAuditLoading = false;
          this.globalLoaderService.resetLoader();
          this.cdr.detectChanges();
        }
      });
    };
    this.statusPollingInterval = setInterval(pollStatus, initialInterval);
    pollStatus();
  }

  /**
   * Process Landing Page audit data and populate component properties
   */
  processLandingPageAuditData(data: any): void {
    if (!data) return;

    // Extract data from structured_data if present (from bulk search)
    const actualData = data.structured_data || data;

    // Extract evidence sources from Tavily
    this.evidenceSources = this.transformSourcesData(
      actualData.evidence_sources || 
      data.evidence_sources || 
      []
    );

    // Check if this is a multi-page audit result
    if (actualData.base_domain || actualData.discovered_pages || actualData.results) {
      this.isMultiPageAudit = true;
      this.processMultiPageAuditData(actualData);
      return;
    }

    // Single-page audit (backward compatibility)
    this.isMultiPageAudit = false;

    // Get composite score
    this.compositeScore = actualData.composite_score || 0;

    // Process audit areas (dynamic categories from CSV)
    if (actualData.audit_areas) {
      this.auditAreas = {};
      
      // Extract area scores
      Object.keys(actualData.audit_areas).forEach(category => {
        const areaData = actualData.audit_areas[category];
        this.auditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 10
        };
      });

      // Collect all questions
      this.questionsData = [];
      Object.keys(actualData.audit_areas).forEach(category => {
        const areaData = actualData.audit_areas[category];
        if (areaData.questions && Array.isArray(areaData.questions)) {
          areaData.questions.forEach((q: any) => {
            this.questionsData.push({
              category: category,
              question: q.question || '',
              pass: q.pass || 0,
              evidence: q.evidence || ''
            });
          });
        }
      });
    }

    // Update chart data
    this.updateAreaChart();
  }

  /**
   * Process multi-page audit data
   */
  processMultiPageAuditData(data: any): void {
    this.baseDomain = data.base_domain || '';
    const discoveredPages = data.discovered_pages || [];
    const results = data.results || {};
    const summary = data.summary || {};

    // Initialize discovered pages array - show ALL discovered pages, even without data
    this.discoveredPages = discoveredPages.map((url: string) => {
      const result = results[url];
      
      // Determine status based on result
      let status: 'discovering' | 'pending' | 'auditing' | 'completed' | 'failed' = 'pending';
      let compositeScore: number | undefined = undefined;
      
      if (result) {
        if (result.status === 'pending') {
          status = 'pending';
        } else if (result.status === 'completed') {
          status = 'completed';
          compositeScore = result.composite_score || 0;
        } else if (result.status === 'failed' || result.error) {
          status = 'failed';
        } else if (result.audit_areas && Object.keys(result.audit_areas).length > 0 && result.composite_score !== null && result.composite_score !== undefined) {
          // Valid result
          status = 'completed';
          compositeScore = result.composite_score || 0;
        } else {
          // Has result but incomplete data
          status = 'pending';
        }
      } else {
        // No result yet - pending
        status = 'pending';
      }
      
      return {
        url: url,
        status: status,
        result: result || null,
        compositeScore: compositeScore
      };
    });

    // Update progress
    this.discoveryProgress = {
      status: 'completed',
      pagesFound: discoveredPages.length
    };
    this.auditProgress = {
      total: discoveredPages.length,
      completed: summary.completed || 0,
      current: ''
    };
    this.isDiscovering = false;
    this.isAuditing = false;
  }

  /**
   * Update area chart data for area-chart component
   * Format: [{name: "Current/Potential", series: [{name: "Category", value: score}]}]
   */
  updateAreaChart(): void {
    const categories = Object.keys(this.auditAreas);
    
    const currentSeries = categories.map(category => ({
      name: category,
      value: this.auditAreas[category].current
    }));

    const potentialSeries = categories.map(category => ({
      name: category,
      value: this.auditAreas[category].potential
    }));

    this.areaChartData = [
      {
        name: 'Current',
        series: currentSeries
      },
      {
        name: 'Potential',
        series: potentialSeries
      }
    ];
  }

  /**
   * Get all unique categories from audit areas
   */
  getCategories(): string[] {
    return Object.keys(this.auditAreas);
  }

  /**
   * Get questions for a specific category
   */
  getQuestionsByCategory(category: string): Array<{
    question: string;
    pass: number;
    evidence: string;
  }> {
    return this.questionsData
      .filter(q => q.category === category)
      .map(q => ({
        question: q.question,
        pass: q.pass,
        evidence: q.evidence
      }));
  }

  /**
   * Get only failed questions (pass === 0)
   */
  getFailedQuestions(): Array<{
    category: string;
    question: string;
    pass: number;
    evidence: string;
  }> {
    return this.questionsData.filter(q => q.pass === 0);
  }

  /**
   * Check if score passes (score > 8)
   */
  isPass(score: number): boolean {
    return score > 8;
  }

  /**
   * Get score color based on value (0–10 scale used in tables/widgets)
   */
  getScoreColor(score: number): string {
    if (score > 8) return '#4caf50'; // Green (Pass)
    if (score >= 6) return '#ff9800'; // Orange
    if (score >= 4) return '#ff5722'; // Red-Orange
    return '#f44336'; // Red (Fail)
  }

  /**
   * Get URL from audit result
   */
  getUrl(): string {
    if (!this.auditResult) return '';
    const actualData = this.auditResult.structured_data || this.auditResult;
    
    // For multi-page audit, return base domain
    if (this.isMultiPageAudit) {
      return actualData.base_domain || '';
    }
    
    return actualData.url || '';
  }

  /**
   * Toggle accordion expand/collapse
   */
  toggleAccordion(url: string): void {
    if (this.expandedAccordions.has(url)) {
      this.expandedAccordions.delete(url);
    } else {
      this.expandedAccordions.add(url);
    }
  }

  /**
   * Check if accordion is expanded
   */
  isAccordionExpanded(url: string): boolean {
    return this.expandedAccordions.has(url);
  }

  /**
   * Get page results for specific URL
   */
  getPageResults(url: string): any {
    const page = this.discoveredPages.find(p => p.url === url);
    return page?.result || null;
  }

  /**
   * Get average score across all pages
   */
  getAverageScore(): number {
    if (!this.isMultiPageAudit || this.discoveredPages.length === 0) {
      return 0;
    }
    const scores = this.discoveredPages
      .filter(p => p.compositeScore !== undefined && p.status === 'completed')
      .map(p => p.compositeScore || 0);
    if (scores.length === 0) return 0;
    return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100;
  }

  /**
   * Get best page (highest score)
   */
  getBestPage(): { url: string; score: number } | null {
    if (!this.isMultiPageAudit || this.discoveredPages.length === 0) {
      return null;
    }
    const completedPages = this.discoveredPages.filter(p => p.status === 'completed' && p.compositeScore !== undefined);
    if (completedPages.length === 0) return null;
    
    const best = completedPages.reduce((prev, current) => 
      (prev.compositeScore || 0) > (current.compositeScore || 0) ? prev : current
    );
    
    return {
      url: best.url,
      score: best.compositeScore || 0
    };
  }

  /**
   * Get worst page (lowest score)
   */
  getWorstPage(): { url: string; score: number } | null {
    if (!this.isMultiPageAudit || this.discoveredPages.length === 0) {
      return null;
    }
    const completedPages = this.discoveredPages.filter(p => p.status === 'completed' && p.compositeScore !== undefined);
    if (completedPages.length === 0) return null;
    
    const worst = completedPages.reduce((prev, current) => 
      (prev.compositeScore || 0) < (current.compositeScore || 0) ? prev : current
    );
    
    return {
      url: worst.url,
      score: worst.compositeScore || 0
    };
  }

  /**
   * Get summary statistics
   */
  getSummary(): any {
    if (!this.isMultiPageAudit) return null;
    const actualData = this.auditResult?.structured_data || this.auditResult;
    return actualData?.summary || {
      total_pages: this.discoveredPages.length,
      completed: this.getCompletedPagesCount(),
      average_score: this.getAverageScore(),
      best_page: this.getBestPage()?.url || '',
      worst_page: this.getWorstPage()?.url || ''
    };
  }

  /**
   * Get count of completed pages
   */
  getCompletedPagesCount(): number {
    return this.discoveredPages.filter(p => p.status === 'completed').length;
  }

  /**
   * Get categories from page data
   */
  getPageCategories(pageData: any): string[] {
    if (!pageData || !pageData.auditAreas) return [];
    return Object.keys(pageData.auditAreas);
  }

  /**
   * Get questions for a category from page data
   */
  getPageQuestionsByCategory(pageData: any, category: string): Array<{
    question: string;
    pass: number;
    evidence: string;
  }> {
    if (!pageData || !pageData.questionsData) return [];
    return pageData.questionsData
      .filter((q: any) => q.category === category)
      .map((q: any) => ({
        question: q.question,
        pass: q.pass,
        evidence: q.evidence
      }));
  }

  /**
   * Get failed questions from page data
   */
  getPageFailedQuestions(pageData: any): Array<{
    category: string;
    question: string;
    pass: number;
    evidence: string;
  }> {
    if (!pageData || !pageData.questionsData) return [];
    return pageData.questionsData.filter((q: any) => q.pass === 0);
  }

  /**
   * Process single page audit data (for accordion content)
   */
  processSinglePageData(pageData: any): {
    compositeScore: number;
    auditAreas: { [key: string]: { current: number; potential: number } };
    questionsData: Array<{ category: string; question: string; pass: number; evidence: string }>;
    areaChartData: any[];
  } {
    if (!pageData) {
      return {
        compositeScore: 0,
        auditAreas: {},
        questionsData: [],
        areaChartData: []
      };
    }

    const compositeScore = pageData.composite_score || 0;
    const auditAreas: { [key: string]: { current: number; potential: number } } = {};
    const questionsData: Array<{ category: string; question: string; pass: number; evidence: string }> = [];

    if (pageData.audit_areas) {
      Object.keys(pageData.audit_areas).forEach(category => {
        const areaData = pageData.audit_areas[category];
        auditAreas[category] = {
          current: areaData.current_score || 0,
          potential: areaData.potential_score || 10
        };

        if (areaData.questions && Array.isArray(areaData.questions)) {
          areaData.questions.forEach((q: any) => {
            questionsData.push({
              category: category,
              question: q.question || '',
              pass: q.pass || 0,
              evidence: q.evidence || ''
            });
          });
        }
      });
    }

    // Generate chart data
    const categories = Object.keys(auditAreas);
    const currentSeries = categories.map(category => ({
      name: category,
      value: auditAreas[category].current
    }));
    const potentialSeries = categories.map(category => ({
      name: category,
      value: auditAreas[category].potential
    }));
    const areaChartData = [
      { name: 'Current', series: currentSeries },
      { name: 'Potential', series: potentialSeries }
    ];

    return {
      compositeScore,
      auditAreas,
      questionsData,
      areaChartData
    };
  }

  /**
   * Get passed count for questions array
   */
  getPassedCount(questions: Array<{ question: string; pass: number; evidence: string }>): number {
    if (!questions) return 0;
    return questions.filter(q => q.pass === 1).length;
  }

  /**
   * Get total count for questions array
   */
  getTotalCount(questions: Array<{ question: string; pass: number; evidence: string }>): number {
    if (!questions) return 0;
    return questions.length;
  }
  
  /**
   * Re-run Landing Page audit using task-based API with status polling
   */
  reAudit(): void {
    // Get project and filter data
    const projectData = this.genericTemplateCommonService.getCurrentProjectData();
    const filterData = this.genericTemplateCommonService.getCurrentFilterData();
    const projectId = this.projectId || projectData?.id;
    
    // Get projectdata_id from URL or service if available
    const urlParams = new URLSearchParams(window.location.search);
    const projectdataId = urlParams.get('projectdataId') || undefined;
    
    // Build filters for audit
    const brands = filterData?.selectedEntities?.map(e => e.name) || [];
    const country = filterData?.country || 'Global';
    const category = filterData?.category || 'General';
    
    // Extract base domain from project description for multi-page audit
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    
    const filters: any = {
      country: country,
      category: category,
      industry: 'General'
    };
    
    if (brands.length > 0) {
      filters.brands = brands;
      filters.brand = brands[0];
    }
    
    if (projectDescription) {
      filters.project_description = projectDescription;
    }
    
    if (baseDomain) {
      filters.base_domain = baseDomain;
      filters.max_pages = 50;
    }
    
    // Start task-based audit
    this.startTaskBasedAudit({
      project_id: projectId,
      projectdata_id: projectdataId,
      filters: filters
    });
    
    // Show snackbar notification
    this.snackBar.open(
      'Landing Page audit started. Progress will be shown below.',
      'Close',
      { duration: 5000 }
    );
  }

  /**
   * Extract base domain from project description
   * Helper method to extract base domain for re-audit
   */
  private extractBaseDomain(description: string | null | undefined): string | null {
    if (!description) return null;
    
    try {
      // Pattern 1: Full URLs (http:// or https://)
      const urlPattern = /https?:\/\/[^\s\)\]\>\"\']+/;
      const urlMatches = description.match(urlPattern);
      
      if (urlMatches && urlMatches.length > 0) {
        const url = urlMatches[0].replace(/[.,;:!?)]+$/, ''); // Remove trailing punctuation
        try {
          const urlObj = new URL(url);
          // Return base domain (scheme + netloc)
          return `${urlObj.protocol}//${urlObj.host}`;
        } catch (e) {
          // Invalid URL, try next pattern
        }
      }
      
      // Pattern 2: Domain patterns (www.example.com or example.com)
      const domainPattern = /(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+/;
      const domainMatches = description.match(domainPattern);
      
      if (domainMatches && domainMatches.length > 0) {
        const domain = domainMatches[0];
        // Construct URL with https scheme
        return `https://${domain}`;
      }
      
      return null;
    } catch (e) {
      console.warn('Error extracting base domain:', e);
      return null;
    }
  }

  private transformSourcesData(sources: any[]): any[] {
    return sources.map((source, index) => ({
      title: source.title || source.snippet || `Source ${index + 1}`,
      url: source.url,
      confidence: source.confidence || 0.7,
      snippet: source.snippet || source.title,
      source: source.source || "Tavily",
      category: 'landing_page_audit',
      id: `source_${index}`
    }));
  }

  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  /**
   * Load audit data from history without page refresh
   */
  /**
   * Load audit data from history API (only called on page load, not after audit completion)
   * After audit completion, data comes directly from status.result
   */
  private loadAuditDataFromHistory(projectdataId: string): void {
    this.loading = true; // Show loading while fetching from history
    this.projectHistoryService.getVersionData(projectdataId).subscribe({
      next: (versionData) => {
        // Update only landing-page-audit data
        if (versionData.data && versionData.data['landing-page-audit']) {
          const auditData = versionData.data['landing-page-audit'];
          let extractedData: any = null;
          
          // Extract structured_data if available
          if (auditData.structured_data) {
            extractedData = auditData.structured_data;
          } else if (auditData.response && typeof auditData.response === 'string' && auditData.response.trim().startsWith('{')) {
            try {
              extractedData = JSON.parse(auditData.response);
            } catch (e) {
              console.warn('Failed to parse audit response JSON:', e);
            }
          } else {
            extractedData = auditData;
          }
          
          if (extractedData) {
            this.auditResult = extractedData;
            this.processLandingPageAuditData(extractedData);
            this.loading = false;
            this.cdr.detectChanges();
            
            // Also update the service so other components can access it
            this.genericTemplateCommonService.updateLandingPageAuditResponse(extractedData);
          } else {
            this.loading = false;
          }
        } else {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Error loading audit data from history:', err);
        this.loading = false;
        // Don't show error notification on page load - just silently fail
        // User can still trigger a new audit if needed
      }
    });
  }

}
